/**
 * Simplified Blog Service for fetching blog data from Azure Blob Storage
 * Uses SAS tokens for authentication when listing and fetching items from container
 */

import { getConfiguredSasToken } from '../config/blobStorageConfig.js';

const BLOG_BASE_URL = 'https://starapps.blob.core.windows.net/multi-sites';

/**
 * Get SAS token for authentication
 * @returns {Promise<string|null>} SAS token or null if not available
 */
const getSasToken = async () => {
  try {
    return await getConfiguredSasToken();
  } catch (error) {
    return null;
  }
};

/**
 * Fetch a list of all blog files from the blob storage container
 * @returns {Promise<Array>} Array of blog metadata
 */
export const fetchBlogList = async () => {
  try {
    // Get SAS token for authentication
    const sasToken = await getSasToken();
    
    // Construct the URL for listing blobs with SAS token
    let listUrl = `${BLOG_BASE_URL}?restype=container&comp=list&prefix=blogs`;
    if (sasToken) {
      listUrl += `&${sasToken}`;
    }
    
    // Try to list all blobs in the container
    const response = await fetch(listUrl);
    
    if (response.ok) {
      const xmlText = await response.text();
      
      // Parse the XML response to get blob names
      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(xmlText, 'text/xml');
      
      // Get all blob names
      const nameElements = xmlDoc.getElementsByTagName('Name');
      const blobNames = Array.from(nameElements).map(element => element.textContent);
      
      // Filter for blog files (any JSON files in the blogs/ folder)
      const blogFilenames = blobNames.filter(name => 
        name && name.endsWith('.json') && name.startsWith('blogs/')
      );
      
      // Remove the 'blogs/' prefix from filenames for fetching
      const cleanFilenames = blogFilenames.map(name => name.replace('blogs/', ''));
      
      // Fetch metadata for each blog file
      return await fetchBlogMetadata(cleanFilenames, sasToken);
    }
    
    // If container listing fails, return empty array
    return [];
    
  } catch (error) {
    console.error('Error fetching blog list:', error);
    return [];
  }
};

/**
 * Fetch metadata for a list of blog filenames
 * @param {Array<string>} filenames - Array of blog filenames
 * @param {string|null} sasToken - SAS token for authentication
 * @returns {Promise<Array>} Array of blog metadata
 */
const fetchBlogMetadata = async (filenames, sasToken = null) => {
  const blogPromises = filenames.map(async (filename) => {
    try {
      // Construct URL with SAS token (include blogs/ prefix in the path)
      let blogUrl = `${BLOG_BASE_URL}/blogs/${filename}`;
      if (sasToken) {
        blogUrl += `?${sasToken}`;
      }
      
      const response = await fetch(blogUrl);
      if (response.ok) {
        const blogData = await response.json();
        return {
          id: blogData.id,
          author: blogData.author,
          title: blogData.title,
          slug: blogData.slug,
          date: blogData.date,
          excerpt: blogData.excerpt,
          image: blogData.image,
          filename: filename
        };
      }
      return null;
    } catch (error) {
      console.warn(`Failed to fetch blog ${filename}:`, error);
      return null;
    }
  });

  const blogs = await Promise.all(blogPromises);
  return blogs.filter(blog => blog !== null).sort((a, b) => new Date(b.date) - new Date(a.date));
};

/**
 * Fetch a specific blog by its filename
 * @param {string} filename - The blog filename (e.g., 'blog-2025-09-19.json')
 * @returns {Promise<Object|null>} The blog data or null if not found
 */
export const fetchBlogByFilename = async (filename) => {
  try {
    // Get SAS token for authentication
    const sasToken = await getSasToken();
    
    // Construct URL with SAS token (include blogs/ prefix in the path)
    let blogUrl = `${BLOG_BASE_URL}/blogs/${filename}`;
    if (sasToken) {
      blogUrl += `?${sasToken}`;
    }
    
    const response = await fetch(blogUrl);
    if (response.ok) {
      return await response.json();
    }
    return null;
  } catch (error) {
    console.error(`Error fetching blog ${filename}:`, error);
    return null;
  }
};

/**
 * Fetch a specific blog by its slug
 * @param {string} slug - The blog slug
 * @returns {Promise<Object|null>} The blog data or null if not found
 */
export const fetchBlogBySlug = async (slug) => {
  try {
    // First, get the list of blogs to find the one with matching slug
    const blogs = await fetchBlogList();
    const blog = blogs.find(b => b.slug === slug);
    
    if (blog) {
      return await fetchBlogByFilename(blog.filename);
    }
    return null;
  } catch (error) {
    console.error(`Error fetching blog by slug ${slug}:`, error);
    return null;
  }
};

/**
 * Get blog URL for a specific filename
 * @param {string} filename - The blog filename
 * @returns {Promise<string>} The full URL to the blog file with SAS token
 */
export const getBlogUrl = async (filename) => {
  const sasToken = await getSasToken();
  let url = `${BLOG_BASE_URL}/blogs/${filename}`;
  if (sasToken) {
    url += `?${sasToken}`;
  }
  return url;
};

/**
 * Check if blob storage is accessible
 * @returns {Promise<Object>} Storage accessibility info
 */
export const checkBlobStorageAccess = async () => {
  try {
    // Get SAS token for authentication
    const sasToken = await getSasToken();
    
    // Construct URL with SAS token
    let listUrl = `${BLOG_BASE_URL}?restype=container&comp=list`;
    if (sasToken) {
      listUrl += `&${sasToken}`;
    }
    
    const response = await fetch(listUrl);
    return {
      accessible: response.ok,
      status: response.status,
      statusText: response.statusText,
      url: BLOG_BASE_URL,
      hasSasToken: !!sasToken
    };
  } catch (error) {
    return {
      accessible: false,
      error: error.message,
      url: BLOG_BASE_URL,
      hasSasToken: false
    };
  }
};

/**
 * Format date for display
 * @param {string} dateString - Date string in YYYY-MM-DD format
 * @returns {string} Formatted date string
 */
export const formatBlogDate = (dateString) => {
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  } catch (error) {
    return dateString;
  }
};

/**
 * Generate reading time estimate based on content
 * @param {Array} sections - Array of blog sections
 * @param {string} conclusion - Blog conclusion text
 * @returns {string} Estimated reading time
 */
export const estimateReadingTime = (sections, conclusion) => {
  const wordsPerMinute = 200;
  let totalWords = 0;
  
  if (sections && Array.isArray(sections)) {
    sections.forEach(section => {
      if (section.content) {
        totalWords += section.content.split(' ').length;
      }
    });
  }
  
  if (conclusion) {
    totalWords += conclusion.split(' ').length;
  }
  
  const minutes = Math.ceil(totalWords / wordsPerMinute);
  return `${minutes} min read`;
};